
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, PlusCircle, Tag } from 'lucide-react';
import { ArticleTag } from '@/types/article-types';

interface TagSelectorProps {
  availableTags: ArticleTag[];
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  onCreateTag: (name: string) => Promise<ArticleTag | null>;
}

const TagSelector = ({ 
  availableTags, 
  selectedTags, 
  onChange, 
  onCreateTag 
}: TagSelectorProps) => {
  const [newTagName, setNewTagName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleAddTag = (tagId: string) => {
    if (!selectedTags.includes(tagId)) {
      onChange([...selectedTags, tagId]);
    }
    setSearchTerm('');
  };

  const handleRemoveTag = (tagId: string) => {
    onChange(selectedTags.filter(id => id !== tagId));
  };

  const handleCreateTag = async () => {
    if (newTagName.trim()) {
      const newTag = await onCreateTag(newTagName.trim());
      if (newTag) {
        handleAddTag(newTag.id);
        setNewTagName('');
        setIsCreating(false);
      }
    }
  };

  const filteredTags = availableTags.filter(tag => 
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedTags.includes(tag.id)
  );

  const selectedTagObjects = selectedTags
    .map(id => availableTags.find(tag => tag.id === id))
    .filter((tag): tag is ArticleTag => tag !== undefined);

  return (
    <div className="space-y-2">
      {/* Selected tags */}
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedTagObjects.map(tag => (
          <Badge key={tag.id} variant="secondary" className="flex items-center gap-1">
            <Tag className="h-3 w-3" />
            {tag.name}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 ml-1"
              onClick={() => handleRemoveTag(tag.id)}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
      </div>

      {/* Tag search or create */}
      {isCreating ? (
        <div className="flex gap-2">
          <Input
            placeholder="New tag name"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
          />
          <Button 
            type="button" 
            size="sm" 
            onClick={handleCreateTag}
            disabled={!newTagName.trim()}
          >
            Add
          </Button>
          <Button 
            type="button" 
            size="sm" 
            variant="ghost" 
            onClick={() => {
              setIsCreating(false);
              setNewTagName('');
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="relative">
          <Input
            placeholder="Search or add tags"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-2 top-2"
              onClick={() => setIsCreating(true)}
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              Create "{searchTerm}"
            </Button>
          )}
        </div>
      )}

      {/* Tag suggestions */}
      {searchTerm && !isCreating && (
        <div className="border rounded-md p-2 max-h-40 overflow-y-auto">
          {filteredTags.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {filteredTags.map(tag => (
                <Badge 
                  key={tag.id} 
                  variant="outline" 
                  className="cursor-pointer hover:bg-secondary"
                  onClick={() => handleAddTag(tag.id)}
                >
                  {tag.name}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center">
              No matching tags. Create a new one?
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TagSelector;
